
import { Link } from 'react-router-dom';
import { transformThumbnail } from '@/utils/cloudinary';
import { Sprout, Sun, ShoppingCart } from 'lucide-react';
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useVariantImages } from '@/hooks/useVariantImages';
import { BundleSplitColorSwatch } from '@/components/product/BundleSplitColorSwatch';
import { useCart } from '@/context/CartContext';
import { toast } from '@/hooks/use-toast';

interface ColorInfo {
  name: string;
  hex: string;
  childHexColors?: string[];
}

interface ProductCardProps {
  id: string;
  name: string;
  price: number;
  image: string;
  images?: string[];
  availableColors?: any;
  discountPrice?: number;
  isDiscounted?: boolean;
  categories?: string[];
  defaultVariantSize?: string;
  defaultVariantPotType?: string;
  hideColorSelector?: boolean;
  /** Pre-computed colors from parent (Shop page) to avoid per-card DB calls */
  precomputedColors?: ColorInfo[];
  isBundle?: boolean;
}

export const ProductCard = ({ 
  id, 
  name, 
  price, 
  image, 
  images = [],
  availableColors,
  discountPrice, 
  isDiscounted,
  categories = [],
  defaultVariantSize,
  defaultVariantPotType,
  hideColorSelector = false,
  precomputedColors,
  isBundle = false
}: ProductCardProps) => {
  const { addToCart, openCartDrawer } = useCart();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [resolvedSize, setResolvedSize] = useState<string | null>(defaultVariantSize || null);
  const [resolvedPotType, setResolvedPotType] = useState<string | null>(defaultVariantPotType || null);
  const [currentDisplayImage, setCurrentDisplayImage] = useState<string>(image);
  const displayPrice = isDiscounted && discountPrice ? discountPrice : price;
  const originalPrice = isDiscounted && discountPrice ? price : null;
  
  // Skip fetching if precomputed data was provided (Shop page scenario)
  const skipFetch = !!precomputedColors;
  
  // Use the variant images hook for color switching (only fires when a color is selected)
  const { imageUrls: variantImageUrls, hasVariantImages, isLoading } = useVariantImages(
    id,
    resolvedSize || undefined,
    resolvedPotType || undefined,
    selectedColor || undefined
  );
  
  const imagesToShow = images.length > 0 ? images : [image];
  
  // Update displayed image when variant images load or color is cleared
  useEffect(() => {
    if (selectedColor && hasVariantImages && variantImageUrls.length > 0 && !isLoading) {
      setCurrentDisplayImage(variantImageUrls[0]);
    } else if (!selectedColor) {
      setCurrentDisplayImage(imagesToShow[currentImageIndex]);
    }
  }, [selectedColor, hasVariantImages, variantImageUrls, isLoading, currentImageIndex, imagesToShow]);
  
  const transformedImage = transformThumbnail(currentDisplayImage);
  
  // Fetch default variant info ONLY if not provided and not precomputed
  useEffect(() => {
    if (skipFetch) return;
    if (defaultVariantSize && defaultVariantPotType) {
      setResolvedSize(defaultVariantSize);
      setResolvedPotType(defaultVariantPotType);
      return;
    }
    
    const fetchDefaultVariant = async () => {
      const { data, error } = await supabase
        .from('product_variants')
        .select('size, pot_type, price, discount_price, is_discounted, stock')
        .eq('product_id', id)
        .order('price', { ascending: true })
        .limit(10);
      
      if (!error && data && data.length > 0) {
        const inStockVariant = data.find(v => v.stock > 0) || data[0];
        setResolvedSize(inStockVariant.size);
        setResolvedPotType(inStockVariant.pot_type);
      }
    };
    
    fetchDefaultVariant();
  }, [id, defaultVariantSize, defaultVariantPotType, skipFetch]);
  
  // State for dynamically fetched colors (only used when precomputedColors not provided)
  const [fetchedColors, setFetchedColors] = useState<ColorInfo[]>([]);
  
  // Fetch variant colors and hex codes ONLY if not precomputed
  useEffect(() => {
    if (skipFetch || !resolvedSize || !resolvedPotType) return;
    
    const loadColorsAndHex = async () => {
      const { data: variantData } = await supabase
        .from('product_variants')
        .select('pot_color')
        .eq('product_id', id)
        .eq('size', resolvedSize)
        .eq('pot_type', resolvedPotType);
      
      if (variantData) {
        const uniqueColors = [...new Set(variantData.map(v => v.pot_color).filter((c): c is string => c !== null))];
        
        const { data: attrData } = await supabase
          .from('attribute_values')
          .select('name, metadata')
          .eq('product_id', id)
          .in('name', uniqueColors);
        
        const hexMap = new Map<string, string>();
        if (attrData) {
          attrData.forEach((attr: any) => {
            const hex = attr.metadata?.hexColor || attr.metadata?.hex;
            if (hex) hexMap.set(attr.name.toLowerCase(), hex);
          });
        }
        
        setFetchedColors(uniqueColors.map(c => ({
          name: c,
          hex: hexMap.get(c.toLowerCase()) || getColorHex(c),
        })));
      }
    };
    
    loadColorsAndHex();
  }, [id, resolvedSize, resolvedPotType, skipFetch]);
  
  // Use precomputed colors if available, otherwise use fetched
  const colors = precomputedColors || fetchedColors;

  // Check for special features
  const isLowLight = categories.some(cat => cat.toLowerCase().includes('low light'));
  const isPetFriendly = categories.some(cat => cat.toLowerCase().includes('pet friendly'));

  return (
    <Link to={`/product/${id}`} className="group block">
      <div className="relative">
        {/* Image Container */}
        <div className="relative aspect-square overflow-hidden rounded-xl bg-background mb-2">
          <img
            loading="lazy"
            src={transformedImage}
            alt={name}
            className="h-full w-full object-contain p-4 transition-transform group-hover:scale-105"
            onError={(e) => {
              e.currentTarget.src = "/placeholder.svg";
            }}
          />
          
          {/* Discount Badge */}
          {isDiscounted && originalPrice && (
            <div className="absolute top-3 left-3 bg-black text-white rounded-full w-16 h-16 flex flex-col items-center justify-center text-center leading-tight">
              <span className="text-sm font-bold">₹{originalPrice - displayPrice} OFF</span>
              <span className="text-[10px]">3 Small</span>
              <span className="text-[10px]">Plants</span>
            </div>
          )}
          
          {/* Feature Badges */}
          <div className="absolute top-3 right-3 flex flex-col gap-1">
            {isPetFriendly && (
              <div className="bg-white/90 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1 text-[10px]">
                <Sprout className="w-3 h-3" />
                <span>Pet Friendly</span>
              </div>
            )}
            {isLowLight && (
              <div className="bg-white/90 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1 text-[10px]">
                <Sun className="w-3 h-3" />
                <span>Low Light</span>
              </div>
            )}
          </div>
          
          {/* Image Carousel Dots */}
          {imagesToShow.length > 1 && (
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1">
              {imagesToShow.map((_, idx) => (
                <button
                  key={idx}
                  onClick={(e) => {
                    e.preventDefault();
                    setCurrentImageIndex(idx);
                  }}
                  className={`w-1 h-1 rounded-full transition-colors ${
                    idx === currentImageIndex ? 'bg-foreground' : 'bg-foreground/30'
                  }`}
                  aria-label={`View image ${idx + 1}`}
                />
              ))}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div>
          <div className="flex items-start justify-between gap-1">
            <h3 className="text-sm font-medium text-foreground mb-0.5 line-clamp-1 flex-1">
              {name}
            </h3>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const productForCart = {
                  id,
                  name,
                  price: displayPrice,
                  image_urls: images.length > 0 ? images : [image],
                  category: null,
                  currency: 'INR',
                  description: '',
                  stock: 999,
                  is_published: true,
                  glance_info: null,
                  is_discounted: isDiscounted || false,
                  discount_price: discountPrice || null,
                  include_in_deals: false,
                  available_colors: availableColors,
                };
                const variantInfo = resolvedSize && resolvedPotType ? {
                  pot_type: resolvedPotType,
                  pot_color: selectedColor || 'default',
                  size: resolvedSize,
                } : undefined;
                addToCart(productForCart, undefined, resolvedSize || undefined, variantInfo);
                openCartDrawer();
                toast({ title: 'Added to cart', description: name });
              }}
              className="p-1.5 rounded-full bg-primary/10 hover:bg-primary/20 text-primary transition-colors shrink-0"
              aria-label={`Add ${name} to cart`}
            >
              <ShoppingCart className="h-4 w-4" />
            </button>
          </div>
          <p className="text-xs text-muted-foreground">
            From ₹{displayPrice}
          </p>
          
          {/* Color Selector */}
          {!hideColorSelector && colors.length > 0 && (
            <div className="flex gap-1.5 mt-2">
              {colors.map((color) => (
                <button
                  key={color.name}
                  onClick={(e) => {
                    e.preventDefault();
                    setSelectedColor(color.name);
                  }}
                  className={`w-7 h-7 rounded-full border-2 transition-all ${
                    selectedColor === color.name 
                      ? 'border-foreground scale-110' 
                      : 'border-border hover:border-foreground/50'
                  }`}
                  style={isBundle && color.childHexColors && color.childHexColors.length > 1
                    ? undefined
                    : { backgroundColor: color.hex }
                  }
                  aria-label={`Select ${color.name} color`}
                >
                  {isBundle && color.childHexColors && color.childHexColors.length > 1 && (
                    <BundleSplitColorSwatch colors={color.childHexColors} className="w-full h-full" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

// Fallback hex colors
const getColorHex = (colorName: string): string => {
  const colorMap: Record<string, string> = {
    white: "#FFFFFF", black: "#000000", brown: "#8B4513", terracotta: "#E2725B",
    grey: "#808080", beige: "#E3DACE", charcoal: "#595959", stone: "#F1F1F1",
    mustard: "#FFDB58", "light-grey": "#D3D3D3", "forest-green": "#228B22",
    navy: "#000080", yellow: "#FFD700", orange: "#FF8C00", red: "#DC2626",
  };
  return colorMap[colorName?.toLowerCase()] || "#CCCCCC";
};
